/**
 * Star Force Enhancement Module
 * MapleStory-style equipment enhancement system
 */

import {
  STAR_FORCE_CONFIG,
  getEnhancementCost,
  getSuccessRate,
  canUseSafeguard,
  calculateStarForceStats,
  getFailureResult,
} from '../../data/starForce';

/**
 * Attempt to enhance an equipment item
 * @param {Object} item - The equipment item to enhance
 * @param {number} starEssence - Player's current Star Essence
 * @param {boolean} safeguard - Whether safeguard is enabled
 * @returns {{ success: boolean, newItem?: Object, starEssenceSpent: number, result: 'success'|'fail_stay'|'fail_downgrade', error?: string }}
 */
export function attemptEnhancement(item, starEssence, safeguard = false) {
  // Validate item can be enhanced
  if (!item || !item.slot) {
    return { success: false, starEssenceSpent: 0, result: 'error', error: 'Item cannot be enhanced' };
  }

  const currentStars = item.starForce || 0;

  // Check max stars
  if (currentStars >= STAR_FORCE_CONFIG.maxStars) {
    return { success: false, starEssenceSpent: 0, result: 'error', error: 'Item is already at max stars' };
  }

  // Check safeguard availability
  if (safeguard && !canUseSafeguard(currentStars)) {
    return { success: false, starEssenceSpent: 0, result: 'error', error: 'Safeguard not available for this star level' };
  }

  // Calculate cost
  const cost = getEnhancementCost(currentStars, item.rarity, safeguard);
  if (starEssence < cost) {
    return { success: false, starEssenceSpent: 0, result: 'error', error: `Not enough Star Essence (need ${cost})` };
  }

  // Get success rate with pity bonus
  const consecutiveFailures = item.consecutiveFailures || 0;
  const successRate = getSuccessRate(currentStars, consecutiveFailures);

  // Roll for success
  const roll = Math.random();
  const isSuccess = roll < successRate;

  if (isSuccess) {
    // Success - increase stars, recalculate stats, reset pity
    const newStars = currentStars + 1;
    const newStarForceStats = calculateStarForceStats(newStars, item.rarity);

    const newItem = {
      ...item,
      starForce: newStars,
      starForceStats: newStarForceStats,
      consecutiveFailures: 0, // Reset pity on success
    };

    return {
      success: true,
      newItem,
      starEssenceSpent: cost,
      result: 'success',
      previousStars: currentStars,
      newStars,
      roll,
      successRate,
    };
  } else {
    // Failure - determine if stars drop
    const newStars = getFailureResult(currentStars, safeguard);
    const starsDropped = currentStars - newStars;
    const newStarForceStats = calculateStarForceStats(newStars, item.rarity);

    const newItem = {
      ...item,
      starForce: newStars,
      starForceStats: newStarForceStats,
      consecutiveFailures: (consecutiveFailures || 0) + 1, // Increase pity counter
    };

    return {
      success: false,
      newItem,
      starEssenceSpent: cost,
      result: starsDropped > 0 ? 'fail_downgrade' : 'fail_stay',
      previousStars: currentStars,
      newStars,
      roll,
      successRate,
    };
  }
}

/**
 * Calculate total stats including star force bonuses
 * @param {Object} item - Equipment item with starForce and starForceStats
 * @returns {Object} Combined stats
 */
export function calculateTotalItemStats(item) {
  if (!item) return {};

  const baseStats = item.stats || {};
  const starStats = item.starForceStats || {};

  // Map star force stats to item stat names
  const statMapping = {
    attack: 'attack',
    defense: 'defense',
    health: 'health',
    critChance: 'critChance',
    critDamage: 'critDamage',
  };

  const combined = { ...baseStats };

  // Add star force bonuses
  Object.entries(starStats).forEach(([stat, value]) => {
    const itemStat = statMapping[stat];
    if (itemStat && value) {
      combined[itemStat] = (combined[itemStat] || 0) + value;
    }
  });

  // allStats bonus applies percentage increase
  if (starStats.allStats) {
    // This would be applied in getPlayerStats() for primary stats
    combined.allStatsBonus = starStats.allStats;
  }

  return combined;
}

/**
 * Preview stat changes for next enhancement
 * @param {Object} item - Equipment item
 * @returns {{ currentStats: Object, nextStats: Object, statChanges: Object }}
 */
export function previewEnhancement(item) {
  if (!item || !item.slot) return null;

  const currentStars = item.starForce || 0;
  if (currentStars >= STAR_FORCE_CONFIG.maxStars) return null;

  const currentStarStats = calculateStarForceStats(currentStars, item.rarity);
  const nextStarStats = calculateStarForceStats(currentStars + 1, item.rarity);

  const statChanges = {};
  Object.keys(nextStarStats).forEach(stat => {
    const current = currentStarStats[stat] || 0;
    const next = nextStarStats[stat] || 0;
    if (next !== current) {
      statChanges[stat] = next - current;
    }
  });

  return {
    currentStats: currentStarStats,
    nextStats: nextStarStats,
    statChanges,
  };
}

/**
 * Get enhancement info for display
 * @param {Object} item - Equipment item
 * @param {boolean} safeguard - Whether safeguard is enabled
 * @returns {Object} Enhancement info
 */
export function getEnhancementInfo(item, safeguard = false) {
  if (!item || !item.slot) return null;

  const currentStars = item.starForce || 0;
  const maxStars = STAR_FORCE_CONFIG.maxStars;
  const isMaxed = currentStars >= maxStars;

  if (isMaxed) {
    return {
      currentStars,
      maxStars,
      isMaxed: true,
      canEnhance: false,
    };
  }

  const consecutiveFailures = item.consecutiveFailures || 0;
  const successRate = getSuccessRate(currentStars, consecutiveFailures);
  const cost = getEnhancementCost(currentStars, item.rarity, safeguard);
  const safeguardAvailable = canUseSafeguard(currentStars);
  const preview = previewEnhancement(item);

  // Determine failure behavior
  let failureBehavior;
  if (safeguard && safeguardAvailable) {
    failureBehavior = 'stay';
  } else if (currentStars < STAR_FORCE_CONFIG.downgradeThreshold) {
    failureBehavior = 'stay';
  } else {
    failureBehavior = 'downgrade';
  }

  return {
    currentStars,
    maxStars,
    targetStars: currentStars + 1,
    isMaxed: false,
    canEnhance: true,
    successRate,
    successRatePercent: Math.round(successRate * 100),
    cost,
    safeguardAvailable,
    safeguardCost: safeguardAvailable ? getEnhancementCost(currentStars, item.rarity, true) : null,
    failureBehavior,
    consecutiveFailures,
    pityBonus: Math.min(consecutiveFailures * STAR_FORCE_CONFIG.pity.bonusPerFailure, STAR_FORCE_CONFIG.pity.maxBonus),
    preview,
  };
}

export {
  STAR_FORCE_CONFIG,
  getEnhancementCost,
  getSuccessRate,
  canUseSafeguard,
  calculateStarForceStats,
  getFailureResult,
};

export default {
  attemptEnhancement,
  calculateTotalItemStats,
  previewEnhancement,
  getEnhancementInfo,
};
